import { Truck } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Marketing block showing our 3-tier free shipping policy.
 * Reused on Landing, Cart, and any high-visibility area.
 * Variants:
 *   - "hero"     : big colored card (Landing page after Hero)
 *   - "compact"  : slim badge (Cart page, above payment)
 *   - "banner"   : full-width strip (site-wide)
 */
export default function ShippingInfoBlock({ variant = "hero" }) {
  const tiers = [
    { zone: "France & Europe", detail: "UE, Royaume-Uni, Suisse", amount: "100 €", tone: "sage" },
    { zone: "Afrique", detail: "tous les pays d'Afrique", amount: "150 €", tone: "coral" },
    { zone: "Reste du monde", detail: "Amériques, Asie, Océanie…", amount: "200 €", tone: "pedablue" },
  ];

  if (variant === "banner") {
    return (
      <div
        className="bg-sage/15 border-y border-sage/25 text-ink py-2.5 text-center text-xs md:text-sm"
        data-testid="shipping-banner"
      >
        <div className="max-w-[1400px] mx-auto px-4 flex flex-col md:flex-row justify-center items-center gap-2 md:gap-8">
          <span className="inline-flex items-center gap-2">
            <Truck size={14} className="text-sage-dark" />
            <strong>Livraison offerte</strong>
          </span>
          <span>France & Europe dès <strong>100 €</strong></span>
          <span className="hidden md:inline text-ink/40">·</span>
          <span>Afrique dès <strong>150 €</strong></span>
          <span className="hidden md:inline text-ink/40">·</span>
          <span>Reste du monde dès <strong>200 €</strong></span>
        </div>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div
        className="bg-ivory-200 rounded-2xl p-4 text-xs"
        data-testid="shipping-compact"
      >
        <p className="font-medium text-ink mb-2 flex items-center gap-2">
          <Truck size={14} className="text-sage-dark" />
          Livraison offerte selon votre destination
        </p>
        <ul className="space-y-1 text-ink-soft">
          <li>
            <strong className="text-ink">France & Europe</strong> (UE, UK, Suisse) — dès <strong className="text-sage-dark">100 €</strong>
          </li>
          <li>
            <strong className="text-ink">Afrique</strong> — dès <strong className="text-sage-dark">150 €</strong>
          </li>
          <li>
            <strong className="text-ink">Reste du monde</strong> — dès <strong className="text-sage-dark">200 €</strong>
          </li>
        </ul>
        <p className="mt-2 italic text-[10px] text-ink-soft">Sur sous-total HT (hors TVA)</p>
      </div>
    );
  }

  // Default: hero variant (large card)
  return (
    <section
      className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16"
      data-testid="shipping-info-hero"
    >
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-sage-dark mb-3">Livraison offerte partout dans le monde</p>
        <h2 className="font-display text-3xl md:text-5xl mb-3">
          On expédie <em className="italic text-sage-dark">chez toi</em>,
          <br className="hidden md:block" /> où que tu sois.
        </h2>
        <p className="text-ink-soft max-w-2xl mx-auto">
          Trois seuils pour trois zones — parce qu&apos;un colis pour Nairobi ne coûte pas le même prix qu&apos;un colis
          pour Lille. On absorbe les frais dès que tu passes le palier.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {tiers.map((t) => (
          <div
            key={t.zone}
            className={`rounded-3xl p-6 border-2 border-${t.tone}/30 bg-${t.tone}/10 relative overflow-hidden`}
            data-testid={`shipping-tier-${t.tone}`}
          >
            <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-${t.tone}/20`} />
            <Truck size={20} className={`text-${t.tone}-dark relative z-10`} />
            <p className="mt-3 font-display text-xl md:text-2xl leading-tight">
              {t.zone}
            </p>
            <p className="text-xs text-ink-soft mt-1">{t.detail}</p>
            <p className="mt-4 text-xs uppercase tracking-widest text-ink/60">Livraison offerte dès</p>
            <p className={`text-4xl md:text-5xl font-display font-light text-${t.tone}-dark`}>{t.amount}</p>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-ink-soft mt-6">
        Sous-total HT (hors TVA). Détails complets dans nos{" "}
        <Link to="/cgv" className="underline hover:text-coral">CGV — Article 3</Link>.
      </p>
    </section>
  );
}
